/*                                         */
import type { ChatMessage } from "../types/ChatMessage";
import { isCompletableMessage, isMessageCompleted, type Message } from "../types/Websocket";
import { getStorage } from "../lib/CookieConsent";
import { CHAT_MESSAGES_LOCAL_STORAGE_KEY } from "../config";
import { sendCustomError } from "../lib/ApplicationPerormanceMonitoring";

const MAX_HISTORY_LENGTH = 500;

export const chatMessages: ChatMessage[] = $state([]);
let chatMessagesBuffer: ChatMessage[] = [];

/**
 *
 */
(async function initializeChatMessages() {
    const messagesFromStorage = await loadChatMessagesFromStorage();
    chatMessages.push(...messagesFromStorage);
})();

/**
 *
 */
export function createNewChatMessage(newMessage: ChatMessage) {
    /*                           */
    if (!newMessage.timestamp) {
        newMessage.timestamp = Date.now();
    }

    /**
 *
 *
 */
    const shouldAddToBuffer = chatMessagesBuffer.length > 0 || newMessage.type === "assistant.product_list";

    if (shouldAddToBuffer) {
        chatMessagesBuffer.push(newMessage);
    } else {
        chatMessages.push(newMessage);
    }

    /*                   */
    if (chatMessages.length > MAX_HISTORY_LENGTH) {
        chatMessages.splice(0, chatMessages.length - MAX_HISTORY_LENGTH);
    }
}

/**
 *
 *
 *
 *
 *
 */
export function updateChatWithMessage(message: Message, sender: ChatMessage["sender"] = "assistant") {
    const latestMessage = chatMessagesBuffer.length > 0 ? chatMessagesBuffer[chatMessagesBuffer.length - 1] : chatMessages[chatMessages.length - 1];
    const lastMessageHasCompleted = !!latestMessage?.message_completed;
    const isNewMessageType = latestMessage?.type !== message.type;

    if (!latestMessage || latestMessage.sender === "user" || isNewMessageType || lastMessageHasCompleted) {
        createNewChatMessage({
            event_id: message.event_id,
            type: message.type,
            message: message.message,
            sender,
            message_completed: isCompletableMessage(message) ? isMessageCompleted(message) : false,
            quick_response_options: message.type === "assistant.message" ? (message.quick_response_options ?? []) : undefined,
        });
    } else {
        if (typeof latestMessage.message === "string") {
            latestMessage.message += message.message;
            if (message.type === "assistant.message") {
                latestMessage.quick_response_options = [...(latestMessage.quick_response_options ?? []), ...(message.quick_response_options ?? [])];
            }
        }

        if (typeof latestMessage.message === "object") {
            latestMessage.message = message.message;
        }
    }

    if (isCompletableMessage(message) && message.event_completed) {
        saveChatMessagesToStorage();
    }
}

export function updateChatWithBufferedMessages() {
    chatMessagesBuffer.sort((a, b) => {
        if (a.type === "assistant.recommendations" && b.type !== "assistant.recommendations") {
            return -1; /*            */
        } else if (b.type === "assistant.recommendations" && a.type !== "assistant.recommendations") {
            return 1; /*            */
        }
        return 0; /*                                      */
    });

    chatMessagesBuffer.forEach((message) => {
        chatMessages.push(message);
    });

    chatMessagesBuffer = [];
}

/**
 *
 */
async function loadChatMessagesFromStorage(): Promise<ChatMessage[]> {
    const storage = await getStorage();
    try {
        const parsed = storage?.instance.getJson<ChatMessage[]>(CHAT_MESSAGES_LOCAL_STORAGE_KEY);
        if (!parsed) return [initialChatMessage()];

        /*                                                      */
        return parsed.map((msg: ChatMessage) => ({
            ...msg,
            timestamp: msg.timestamp ?? Date.now(),
        }));
    } catch (error) {
        sendCustomError("nucleus-cassandra-client-storage", "Failed to parse chat messages from storage", {
            storage: storage.type,
            error: (error as Error).message,
        });
        return [initialChatMessage()];
    }
}

/**
 *
 */
function initialChatMessage(): ChatMessage {
    return {
        event_id: "initial",
        message:
            "Erhalte persönliche Beratung oder finde ganz einfach die Produkte, die zu dir passen. Sieh dir unsere vorgeschlagenen Fragen an oder stelle weiter unten deine eigenen Fragen.",
        sender: "assistant",
        type: "initial.message",
        message_completed: true,
        timestamp: Date.now(),
    };
}

/**
 *
 */
async function saveChatMessagesToStorage() {
    const storage = await getStorage();
    const success = storage.instance?.setJson<ChatMessage[]>(CHAT_MESSAGES_LOCAL_STORAGE_KEY, chatMessages);
    if (!success) {
        sendCustomError("nucleus-cassandra-client-storage", "Failed to save chat messages to storage", {
            storage: storage?.type,
        });
    }
}

/**
 *
 */
export async function resetChatHistory() {
    chatMessages.length = 0;
    chatMessages.push(initialChatMessage());
    await clearStorage();
}

/**
 *
 */
export async function clearStorage() {
    const storage = (await getStorage()).instance;
    storage.removeItem(CHAT_MESSAGES_LOCAL_STORAGE_KEY);
}
